import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Car, MessageSquare, Star, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useApp';
import type { AppNotification, NotificationPage } from '@/lib/types';

/** How often the badge asks. React Query stops entirely while the tab is in the background. */
const POLL_MS = 60_000;

function iconFor(type: number) {
  switch (type) {
    case 1:
      return <MessageSquare size={15} />;
    case 8:
      return <Star size={15} />;
    case 2:
    case 3:
    case 7:
      return <TriangleAlert size={15} />;
    default:
      return <Car size={15} />;
  }
}

function Row({ item, onNavigate }: { item: AppNotification; onNavigate: () => void }) {
  const body = (
    <>
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-full',
          item.readAt ? 'bg-sunken text-faint' : 'bg-accent-soft text-accent',
        )}
      >
        {iconFor(item.type)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-[13px]',
              item.readAt ? 'text-muted' : 'font-semibold text-body',
            )}
          >
            {item.title}
          </span>
          <span className="shrink-0 text-[10.5px] text-faint">{formatRelative(item.createdAt)}</span>
        </span>
        <span className="mt-0.5 line-clamp-2 block text-[12px] text-faint">{item.body}</span>
      </span>
    </>
  );

  const className = cn(
    'flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition duration-200',
    'hover:bg-sunken',
    !item.readAt && 'bg-accent-soft/35',
  );

  // A notification without a link is still worth reading; it just is not worth clicking.
  return item.link ? (
    <Link to={item.link} viewTransition onClick={onNavigate} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

/**
 * Serbian numeral agreement for "new".
 *
 * One is `novo`, two to four are `nova`, five and up are `novih` - and the teens go back to `novih`
 * whatever their last digit is. Only a screen reader hears this string, which is exactly why it was
 * wrong: nobody looking at the badge would ever have noticed "1 novih".
 */
function newLabel(count: number): string {
  const last = count % 10;
  const lastTwo = count % 100;

  if (last === 1 && lastTwo !== 11) {
    return 'novo';
  }

  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return 'nova';
  }

  return 'novih';
}

/**
 * The bell, and the menu behind it.
 *
 * Two queries rather than one: the badge is polled every minute for every signed-in visitor, and the
 * list is fetched only when somebody actually opens the menu. Answering "how many" should never cost
 * a page of rows.
 */
export function NotificationBell() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  const { data: badge } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api<{ count: number }>('/api/notifications/unread'),
    enabled: Boolean(user),
    refetchInterval: POLL_MS,
  });

  const { data: page } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<NotificationPage>('/api/notifications'),
    enabled: Boolean(user) && open,
  });

  const markRead = useMutation({
    mutationFn: () => api('/api/notifications/read', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  /*
    Marked read on opening, not on clicking each one.

    Opening the menu is the act of reading it — that is what the badge was asking for — and making
    somebody dismiss nine notifications one at a time to clear a nine is busywork disguised as
    control. The rows keep their unread tint until the list is next fetched, so what was new is
    still obvious while it is on screen.
  */
  useEffect(() => {
    if (open && (badge?.count ?? 0) > 0) {
      markRead.mutate();
    }
    // Deliberately keyed on `open` alone: re-running when the count changes would fire again on
    // every poll while the menu happens to be open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) {
    return null;
  }

  const unread = badge?.count ?? 0;
  const items = page?.items ?? [];

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={unread > 0 ? `Obaveštenja (${unread} ${newLabel(unread)})` : 'Obaveštenja'}
        aria-expanded={open}
        className="press relative flex size-10 items-center justify-center rounded-xl text-muted transition duration-300 hover:bg-sunken hover:text-body"
      >
        <Bell size={18} className={cn('transition duration-300', open && 'scale-110 text-accent')} />
        {unread > 0 && (
          <span className="animate-pop-in absolute top-1.5 right-1.5 flex size-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-on-accent">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          /*
            Anchored under the bell on a desktop, and pinned to the viewport on a phone.

            The bell is not the rightmost control in the header - the theme toggle and the menu
            button follow it - so a panel hung off its right edge on a 360-pixel screen runs off the
            left of the display. Below `sm` it spans the width instead, which is what a menu of this
            size wants to do there anyway.
          */
          className={cn(
            'animate-fade-in-down z-50 overflow-hidden rounded-[var(--radius-panel)] border border-default bg-raised shadow-float',
            'max-sm:fixed max-sm:inset-x-3 max-sm:top-[4.25rem]',
            'sm:absolute sm:right-0 sm:mt-2 sm:w-[22rem]',
          )}
        >
          <div className="flex items-center justify-between border-b border-default px-3 py-2.5">
            <span className="text-sm font-semibold text-body">Obaveštenja</span>
            <Link
              to="/poruke"
              viewTransition
              onClick={() => setOpen(false)}
              className="text-[12px] font-medium text-accent transition hover:underline"
            >
              Poruke
            </Link>
          </div>

          <div className="max-h-[min(28rem,60dvh)] overflow-y-auto overscroll-contain">
            {items.length > 0 ? (
              items.map((item) => (
                <Row key={item.id} item={item} onNavigate={() => setOpen(false)} />
              ))
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-faint">
                <BellOff size={26} />
                <p className="text-[13px]">Nema obaveštenja.</p>
                <p className="text-[11.5px]">
                  Javićemo vam kada neko pošalje poruku ili vas oceni kao prodavca.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
